import React, { useState } from "react"
import { Div, Text } from "atomize"
import { AnimatePresence, motion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleDown, faAngleUp, faCircle, faFireAlt, faLightbulb } from "@fortawesome/free-solid-svg-icons"
import { theme } from "../settings/config"
import SegmentedPill from "../atoms/SegmentedPill"
import { CARD_TEXT } from "./lessonCardText"
import { COURT_PAID_BY_COACH, COURT_PAID_BY_PLAYERS } from "../utils/billing.utils"

// Il campo della lezione in un blocco solo: dov'è, con cosa acceso, e a chi
// tocca pagarlo.
//
// Chiuso è una riga, quanto basta per controllare che sia giusto prima di
// confermare. Aperto è quello che serve per capire: la superficie per
// esteso, la scelta di chi paga e il conto che ne viene. Prima erano tre
// pastiglie in fila più due righe di spiegazione sempre lì, anche le
// novantanove volte su cento in cui non c'era niente da capire.
//
// Da chiuso il tocco è uno solo e apre: la scelta di chi paga sposta dei
// soldi, e in una riga alta trentaquattro pixel non può stare accanto a un
// altro bersaglio senza farsi premere per sbaglio.
//
// Nella lista delle lezioni lo stesso blocco non si apre e non si tocca:
// lì il campo è una cosa fatta, e la riga chiusa la racconta già tutta. Lo
// sfondo se lo fa dire da fuori, perché in una scheda bianca deve essere
// quello caldo delle pastiglie che ha intorno, e nel modulo il contrario.
function CourtBlock({
	courtNumber,
	surfaceColor,
	sportIcon,
	usingLights,
	usingHeat,
	courtPaidBy,
	onCourtPaidByChange,
	money,
	hint,
	bg = theme.colors.surface,
	expandable = true,
	fitContent = false,
	m = { y: "0.5rem" },
}) {
	const [isOpen, setOpen] = useState(false)
	// nella lista delle lezioni ha le misure della scheda (vedi lessonCardText)
	const inCard = !expandable

	// Dove si gioca, cosa c'era acceso e a chi tocca pagare sono tre cose
	// diverse: un filo le separa, e c'è solo dove c'è qualcosa da separare.
	const hasExtras = usingLights || usingHeat

	const hairline = (
		<Div w="1px" h="1rem" bg={theme.colors.gunmetal_lighter} m={{ x: "0.2rem" }} />
	)

	return (
		<Div
			bg={bg}
			rounded="15px"
			p={inCard ? "0.4rem 0.65rem" : "0.5rem 0.75rem"}
			m={m}
			w={fitContent ? "fit-content" : "auto"}>
			{/* La riga che c'è sempre: da chiusa è tutto il blocco, da aperta
			    è il suo cappello. Toccarla apre e chiude. */}
			<Div
				d="flex"
				align="center"
				justify="space-between"
				cursor={expandable ? "pointer" : "default"}
				style={{ gap: "0.5rem" }}
				onClick={() => expandable && setOpen(!isOpen)}>
				<Div d="flex" align="center" style={{ gap: "0.4rem", minWidth: 0 }}>
					<Text
						textWeight="bold"
						textColor={theme.colors.gunmetal}
						style={inCard ? { fontSize: CARD_TEXT.line } : undefined}>
						Campo {courtNumber}
					</Text>
					{sportIcon && (
						<FontAwesomeIcon icon={sportIcon} color={theme.colors.gunmetal} />
					)}
					<FontAwesomeIcon icon={faCircle} color={surfaceColor} size="sm" />

					{hasExtras && (
						<>
							{hairline}
							{usingLights && (
								<FontAwesomeIcon icon={faLightbulb} color={theme.colors.yellow} />
							)}
							{usingHeat && (
								<FontAwesomeIcon icon={faFireAlt} color={theme.colors.red} />
							)}
						</>
					)}
				</Div>

				<Div d="flex" align="center" style={{ gap: "0.4rem", flexShrink: 0 }}>
					{/* Da chiuso il riassunto di chi paga sta qui: le stesse
					    parole che dentro sono la scelta. */}
					{!isOpen && courtPaidBy && (
						<>
							{hairline}
							<Text
								textSize="caption"
								textColor={theme.colors.text_light}
								style={inCard ? { fontSize: CARD_TEXT.aside } : undefined}>
								pagato da{" "}
								{courtPaidBy === COURT_PAID_BY_COACH ? "me" : "loro"}
								{money ? ` · ${money}` : ""}
							</Text>
						</>
					)}
					{expandable && (
						<FontAwesomeIcon
							icon={isOpen ? faAngleUp : faAngleDown}
							color={theme.colors.text_light}
						/>
					)}
				</Div>
			</Div>

			<AnimatePresence initial={false}>
				{isOpen && (
					<motion.section
						style={{ overflow: "hidden" }}
						initial="collapsed"
						animate="open"
						exit="collapsed"
						variants={{
							open: { opacity: 1, height: "auto" },
							collapsed: { opacity: 0, height: 0 },
						}}
						transition={{ duration: 0.25, ease: [0.04, 0.62, 0.23, 0.98] }}>
						{courtPaidBy && (
							<>
								<Div
									h="1px"
									bg={theme.colors.background}
									m={{ y: "0.6rem" }}
								/>
								<Div
									d="flex"
									align="center"
									justify="space-between"
									style={{ gap: "0.5rem" }}>
									<SegmentedPill
										label="pagato da"
										options={[
											{ value: COURT_PAID_BY_COACH, text: "me" },
											{ value: COURT_PAID_BY_PLAYERS, text: "loro" },
										]}
										selected={courtPaidBy}
										onChange={onCourtPaidByChange}
									/>
									{money && (
										<Text
											textWeight="bold"
											textColor={theme.colors.gunmetal}
											style={{ whiteSpace: "nowrap" }}>
											{money}
										</Text>
									)}
								</Div>
								{hint && (
									<Text
										textSize="caption"
										textColor={theme.colors.text_light}
										m={{ t: "0.4rem" }}>
										{hint}
									</Text>
								)}
							</>
						)}
					</motion.section>
				)}
			</AnimatePresence>
		</Div>
	)
}

export default CourtBlock
